import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Fade,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HideImageRoundedIcon from '@mui/icons-material/HideImageRounded';
import Lightbox from 'yet-another-react-lightbox';
import { Link } from 'react-router-dom';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { GET_MY_FIND_QUERY } from '../../../gql';
import { formatDate, getMaterialColor } from '../../../helpers';
import { useVocabulary } from '../../../i18n';
import { ColorDot, ImageThumbnail } from '../../design-system';
import { SelectedEntityCloseButton } from '../SelectedEntityCloseButton';

interface FindListProps {
  selectedFind: string;
}

interface FindListItem {
  id: string;
  era?: string | null;
  foundAt?: string | null;
  images?: string[] | null;
  latitude?: number | null;
  longitude?: number | null;
  material?: string | null;
  type?: string | null;
  amcrData?: {
    images?: string[] | null;
  } | null;
}

interface FindListData {
  getMyFind?: FindListItem | null;
}

interface FindListVariables {
  findId: string;
}

interface FindImage {
  slideSrc: string;
  thumbnailSrc: string;
}

const animationSettings = { fade: 150, swipe: 0 };
const contentStyles = { minWidth: 0, flex: 1 };
const headingLinkStyles = (theme: Theme) => ({
  cursor: 'pointer',
  display: 'inline',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.text.primary
      : theme.palette.primary.main,
  fontWeight: 500,
  transition: theme.transitions.create('color', { duration: 140 }),
  '&:hover': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.dark,
  },
});
const headingRowStyles = { minHeight: 20 };
const secondaryTextStyles = { color: 'text.secondary' };
const metadataStyles = {
  color: 'text.secondary',
  cursor: 'default',
  fontSize: 11,
  width: 'fit-content',
};
const clockIconStyles = { fontSize: 13 };
const copyButtonStyles = {
  p: 0.25,
  width: 22,
  height: 22,
  color: 'text.disabled',
  '&:hover': {
    color: 'text.secondary',
  },
};
const copyIconStyles = { fontSize: 14 };
const copiedStyles = { fontSize: 11, fontWeight: 500, color: 'success.main' };
const coordinateControlsStyles = { flex: '0 0 auto' };
const thumbnailButtonStyles = {
  width: 80,
  height: 80,
  p: 0,
  border: 0,
  borderRadius: '4px',
  bgcolor: 'transparent',
  cursor: 'pointer',
  lineHeight: 0,
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
};
const thumbnailPlaceholderStyles = (theme: Theme) => ({
  alignItems: 'center',
  bgcolor: theme.palette.action.hover,
  borderRadius: '4px',
  color:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.65)'
      : theme.palette.text.disabled,
  display: 'flex',
  flex: '0 0 auto',
  height: 80,
  justifyContent: 'center',
  width: 80,
});
const noPhotoIconStyles = { fontSize: 30 };
const findListMessages = defineMessages({
  copyCoordinates: {
    id: 'map.find.copyCoordinates',
    defaultMessage: 'Copy GPS coordinates',
  },
  copied: {
    id: 'map.find.coordinatesCopied',
    defaultMessage: 'Copied',
  },
  openImage: {
    id: 'map.find.openImage',
    defaultMessage: 'Open find image',
  },
  draftFind: {
    id: 'map.find.draftFind',
    defaultMessage: 'Draft find',
  },
  noDating: {
    id: 'map.find.noDating',
    defaultMessage: 'No dating yet',
  },
  noPhoto: {
    id: 'map.find.noPhoto',
    defaultMessage: 'No photo',
  },
});

const getMaterialCssColor = (material: string) => {
  const [red, green, blue] = getMaterialColor(material);

  return `rgb(${red}, ${green}, ${blue})`;
};

const FindList = ({ selectedFind }: FindListProps) => {
  const intl = useIntl();
  const getVocabularyLabel = useVocabulary();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [coordinatesCopied, setCoordinatesCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data, loading } = useQuery<FindListData, FindListVariables>(
    GET_MY_FIND_QUERY,
    {
      variables: { findId: selectedFind },
      fetchPolicy: 'cache-first',
      skip: !selectedFind,
    }
  );
  const find = data?.getMyFind;
  const findImages = useMemo<FindImage[]>(
    () => [
      ...(find?.images || []).map((imageId) => ({
        slideSrc: `https://imagedelivery.net/3zdDZwDRXumMqC2LPbSSXw/${imageId}/public`,
        thumbnailSrc: `https://imagedelivery.net/3zdDZwDRXumMqC2LPbSSXw/${imageId}/w=150`,
      })),
      ...(find?.amcrData?.images || []).map((imageId) => ({
        slideSrc: `https://digiarchiv.aiscr.cz/api/img/medium?size=medium&id=${imageId}`,
        thumbnailSrc: `https://digiarchiv.aiscr.cz/api/img/thumb?id=${imageId}`,
      })),
    ],
    [find?.amcrData?.images, find?.images]
  );
  const slides = useMemo(
    () => findImages.map(({ slideSrc }) => ({ src: slideSrc })),
    [findImages]
  );
  const lightboxLabels = useMemo(
    () => ({
      Close: intl.formatMessage({
        id: 'action.close',
        defaultMessage: 'Close',
      }),
      Previous: intl.formatMessage({
        id: 'image.previous',
        defaultMessage: 'Previous image',
      }),
      Next: intl.formatMessage({
        id: 'image.next',
        defaultMessage: 'Next image',
      }),
    }),
    [intl]
  );
  const coordinates =
    find?.latitude != null && find.longitude != null
      ? `${find.latitude}, ${find.longitude}`
      : null;

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    },
    []
  );

  const handleOpenLightbox = useCallback(() => {
    setLightboxOpen(true);
  }, []);
  const handleCloseLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);
  const handleCopyCoordinates = useCallback(async () => {
    if (!coordinates) {
      return;
    }

    try {
      await navigator.clipboard.writeText(coordinates);
      setCoordinatesCopied(true);

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = setTimeout(() => {
        setCoordinatesCopied(false);
      }, 1500);
    } catch (error) {
      console.error('Copy failed', error);
    }
  }, [coordinates]);

  if (loading || !find) {
    return null;
  }

  const firstImage = findImages[0];
  const foundAt = formatDate(find.foundAt);
  const imageAlt = intl.formatMessage(
    {
      id: 'image.find.alt',
      defaultMessage: 'Find image {number}',
    },
    { number: 1 }
  );

  return (
    <Stack my={1} pr={4} position="relative" data-selected-entity-card>
      <SelectedEntityCloseButton />
      <Stack direction="row" alignItems="flex-start" gap={1.5}>
        {firstImage ? (
          <Box
            component="button"
            type="button"
            aria-label={intl.formatMessage(findListMessages.openImage)}
            onClick={handleOpenLightbox}
            sx={thumbnailButtonStyles}
          >
            <ImageThumbnail
              alt={imageAlt}
              src={firstImage.thumbnailSrc}
              size={80}
            />
          </Box>
        ) : (
          <Box
            role="img"
            aria-label={intl.formatMessage(findListMessages.noPhoto)}
            sx={thumbnailPlaceholderStyles}
          >
            <HideImageRoundedIcon sx={noPhotoIconStyles} />
          </Box>
        )}

        <Stack direction="column" gap={0.25} sx={contentStyles}>
          <Stack
            sx={headingRowStyles}
            direction="row"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
          >
            <MuiLink
              sx={headingLinkStyles}
              to={`/f/${find.id}/edit`}
              component={Link}
              underline="none"
            >
              {find.type
                ? getVocabularyLabel('findType', find.type)
                : intl.formatMessage(findListMessages.draftFind)}
            </MuiLink>

            {find.material && (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <ColorDot color={getMaterialCssColor(find.material)} />
                <Typography component="span" variant="body2">
                  {getVocabularyLabel('material', find.material)}
                </Typography>
              </Stack>
            )}
          </Stack>

          <Typography variant="body2" sx={secondaryTextStyles}>
            {find.era
              ? getVocabularyLabel('era', find.era)
              : intl.formatMessage(findListMessages.noDating)}
          </Typography>

          <Stack
            component="span"
            sx={metadataStyles}
            direction="row"
            alignItems="center"
            gap={0.5}
          >
            <AccessTimeRoundedIcon sx={clockIconStyles} />
            <Typography component="span" fontSize="inherit">
              <FormattedMessage id="map.find.found" defaultMessage="found:" />{' '}
              {foundAt || (
                <FormattedMessage
                  id="map.find.unknownDate"
                  defaultMessage="unknown date"
                />
              )}
            </Typography>
          </Stack>

          {coordinates && (
            <Stack
              component="span"
              sx={metadataStyles}
              direction="row"
              alignItems="center"
              gap={0.5}
            >
              <Stack
                component="span"
                sx={coordinateControlsStyles}
                direction="row"
                alignItems="center"
                gap={0.25}
              >
                <Typography component="span" fontSize="inherit">
                  {coordinates}
                </Typography>
                <Tooltip
                  title={intl.formatMessage(findListMessages.copyCoordinates)}
                >
                  <IconButton
                    type="button"
                    size="small"
                    onClick={handleCopyCoordinates}
                    aria-label={intl.formatMessage(
                      findListMessages.copyCoordinates
                    )}
                    sx={copyButtonStyles}
                  >
                    <ContentCopyIcon sx={copyIconStyles} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Fade in={coordinatesCopied} timeout={150}>
                <Typography
                  component="span"
                  aria-live="polite"
                  sx={copiedStyles}
                >
                  {intl.formatMessage(findListMessages.copied)}
                </Typography>
              </Fade>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Lightbox
        animation={animationSettings}
        slides={slides}
        labels={lightboxLabels}
        open={lightboxOpen}
        close={handleCloseLightbox}
      />
    </Stack>
  );
};

export { FindList };
